<template>
    <div>
        <v-card class="mb-4">
            <v-card-title>
                <v-icon class="mr-2">mdi-upload</v-icon>
                Upload de Arquivo de Devedores
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <v-alert type="info" outlined class="mb-4">
                            <div class="font-weight-bold">Instruções:</div>
                            <ul class="mt-2">
                                <li>
                                    Utilize o modelo baixado na aba anterior
                                </li>
                                <li>Preencha todos os campos obrigatórios</li>
                                <li>
                                    ID TITULO deve ser válido e presente no
                                    sistema
                                </li>
                                <li>
                                    CPF/CNPJ deve ser válido e único por título
                                </li>
                                <li>
                                    PRINCIPAL = 1 em exatamente uma linha por
                                    título
                                </li>
                                <li>
                                    Endereço, Bairro, Cidade, UF e CEP são
                                    obrigatórios
                                </li>
                                <li>Nome deve ter até 45 caracteres</li>
                                <li>CEP deve ter 8 dígitos válidos</li>
                                <li>
                                    E-mail e Telefone são obrigatórios e devem
                                    ter mais de 1 caractere
                                </li>
                                <li>Formato aceito: Excel (.xlsx, .xls)</li>
                                <li>Tamanho máximo: 10MB</li>
                            </ul>
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" md="8">
                        <v-file-input
                            v-model="arquivo"
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xls, .xlsx"
                            label="Selecione o arquivo Excel"
                            prepend-icon="mdi-file-excel"
                            show-size
                            chips
                            outlined
                            @change="onArquivoSelecionado"
                        >
                            <template v-slot:selection="{ text }">
                                <v-chip color="primary" label small>
                                    <v-icon left small>mdi-file-excel</v-icon>
                                    {{ text }}
                                </v-chip>
                            </template>
                        </v-file-input>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-btn
                            color="primary"
                            block
                            :disabled="!arquivo"
                            :loading="processandoArquivo"
                            @click="processarArquivo"
                        >
                            <v-icon class="mr-2">mdi-cog</v-icon>
                            Processar Arquivo
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card v-if="dadosProcessados.length > 0">
            <v-card-title>
                <v-icon class="mr-2">mdi-eye</v-icon>
                Pré-visualização dos Dados
            </v-card-title>

            <v-card-text>
                <v-row class="mb-4">
                    <v-col cols="12" sm="3">
                        <v-card color="blue" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ dadosProcessados.length }}
                                </div>
                                <div>Total de Registros</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="green" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ registrosValidos.length }}
                                </div>
                                <div>Registros Válidos</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="red" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ registrosComErro.length }}
                                </div>
                                <div>Com Erros</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="orange" dark>
                            <v-card-text>
                                <div class="text-h6">
                                    {{ titulosUnicos.length }}
                                </div>
                                <div>Títulos Únicos</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-if="registrosComErro.length > 0" class="mb-4">
                    <v-col cols="12">
                        <v-alert type="warning" outlined class="mb-0">
                            <div class="font-weight-bold">
                                <v-icon class="mr-2">mdi-alert-circle</v-icon>
                                Existem
                                {{ registrosComErro.length }} registro(s) com
                                erro(s)
                            </div>
                            <div class="mt-2">
                                Corrija todos os erros antes de pré-visualizar
                                as alterações. Use o relatório de erros para
                                identificar e corrigir os problemas.
                            </div>
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="filtroStatus"
                            :items="opcoesStatus"
                            label="Filtrar por status"
                            outlined
                            dense
                            clearable
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="filtroTitulo"
                            label="Filtrar por título"
                            outlined
                            dense
                            clearable
                            prepend-inner-icon="mdi-magnify"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-data-table
                    :headers="headersPreVisualizacao"
                    :items="dadosFiltrados"
                    :loading="processandoArquivo"
                    item-key="index"
                    :footer-props="{
                        itemsPerPageOptions: [10, 25, 50, 100],
                        itemsPerPageText: 'Itens por página:'
                    }"
                    class="elevation-1"
                >
                    <template v-slot:[`item.status`]="{ item }">
                        <v-chip
                            :color="item.valido ? 'success' : 'error'"
                            text-color="white"
                            small
                        >
                            {{ item.valido ? 'Válido' : 'Erro' }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.erros`]="{ item }">
                        <div v-if="!item.valido">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        color="error"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <div>
                                    <div v-for="erro in item.erros" :key="erro">
                                        • {{ erro }}
                                    </div>
                                </div>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:[`item.documento`]="{ item }">
                        <span :class="{ 'text--disabled': !item.valido }">
                            {{ formatarDocumento(item.documento) }}
                        </span>
                    </template>

                    <template v-slot:[`item.tipoFormatado`]="{ item }">
                        <v-chip
                            :color="
                                item.principal === 1 ? 'primary' : 'secondary'
                            "
                            text-color="white"
                            small
                        >
                            {{
                                item.principal === 1
                                    ? 'Principal'
                                    : 'Codevedor'
                            }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.informacoes`]="{ item }">
                        <v-tooltip bottom max-width="400">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    color="primary"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-eye
                                </v-icon>
                            </template>
                            <div>
                                <div>
                                    <strong>Endereço:</strong>
                                    {{ item.endereco || 'N/A' }}
                                </div>
                                <div>
                                    <strong>Bairro:</strong>
                                    {{ item.bairro || 'N/A' }}
                                </div>
                                <div>
                                    <strong>Cidade:</strong>
                                    {{ item.cidade || 'N/A' }}
                                </div>
                                <div>
                                    <strong>UF:</strong> {{ item.uf || 'N/A' }}
                                </div>
                                <div>
                                    <strong>CEP:</strong>
                                    {{ item.cep || 'N/A' }}
                                </div>
                                <div>
                                    <strong>Email:</strong>
                                    {{ item.email || 'N/A' }}
                                </div>
                            </div>
                        </v-tooltip>
                    </template>
                </v-data-table>

                <v-row class="mt-4">
                    <v-col cols="12">
                        <v-btn
                            color="primary"
                            :disabled="!podePrevisualizar"
                            :loading="processandoArquivo"
                            @click="abrirModalPreVisualizacao"
                        >
                            <v-icon class="mr-2">mdi-eye</v-icon>
                            Pré-visualizar Alterações ({{
                                registrosValidos.length
                            }}
                            registros)
                        </v-btn>

                        <v-btn
                            color="secondary"
                            class="ml-2"
                            @click="limparDados"
                        >
                            <v-icon class="mr-2">mdi-refresh</v-icon>
                            Limpar
                        </v-btn>

                        <v-btn
                            v-if="registrosComErro.length > 0"
                            color="warning"
                            class="ml-2"
                            @click="baixarRelatorioErros"
                        >
                            <v-icon class="mr-2">mdi-download</v-icon>
                            Baixar Relatório de Erros
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-dialog
            v-model="modalPreVisualizacao"
            max-width="1400px"
            persistent
            scrollable
        >
            <v-card>
                <v-toolbar flat dark color="primary">
                    <v-btn icon dark @click="fecharModalPreVisualizacao">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>Confirmar Alterações</v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pa-4">
                    <v-alert type="info" outlined class="mb-4">
                        <div class="font-weight-bold">
                            Resumo das Alterações:
                        </div>
                        <div class="mt-2">
                            •
                            {{ registrosValidos.length }} devedores/coodevedores
                            serão processados •
                            {{ titulosUnicos.length }} título(s) será(ão)
                            atualizado(s) •
                            {{ registrosParaCriar.length }} novo(s) devedor(es)
                            será(ão) criado(s) •
                            {{ registrosParaAtualizar.length }} devedor(es)
                            será(ão) atualizado(s)
                        </div>
                    </v-alert>

                    <v-data-table
                        :headers="headersModalPreVisualizacao"
                        :items="registrosValidos"
                        item-key="index"
                        :footer-props="{
                            itemsPerPageOptions: [10, 25, 50],
                            itemsPerPageText: 'Itens por página:'
                        }"
                        class="elevation-1"
                    >
                        <template v-slot:[`item.acao`]="{ item }">
                            <v-chip
                                :color="getCorAcao(item.acao)"
                                text-color="white"
                                small
                            >
                                {{ formatarAcao(item.acao) }}
                            </v-chip>
                        </template>

                        <template v-slot:[`item.documento`]="{ item }">
                            {{ formatarDocumento(item.documento) }}
                        </template>

                        <template v-slot:[`item.tipoFormatado`]="{ item }">
                            <v-chip
                                :color="
                                    item.principal === 1
                                        ? 'primary'
                                        : 'secondary'
                                "
                                text-color="white"
                                small
                            >
                                {{
                                    item.principal === 1
                                        ? 'Principal'
                                        : 'Codevedor'
                                }}
                            </v-chip>
                        </template>

                        <template v-slot:[`item.informacoes`]="{ item }">
                            <v-tooltip bottom max-width="400">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        color="primary"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        mdi-eye
                                    </v-icon>
                                </template>
                                <div>
                                    <div>
                                        <strong>Endereço:</strong>
                                        {{ item.endereco || 'N/A' }}
                                    </div>
                                    <div>
                                        <strong>Bairro:</strong>
                                        {{ item.bairro || 'N/A' }}
                                    </div>
                                    <div>
                                        <strong>Cidade:</strong>
                                        {{ item.cidade || 'N/A' }}
                                    </div>
                                    <div>
                                        <strong>UF:</strong>
                                        {{ item.uf || 'N/A' }}
                                    </div>
                                    <div>
                                        <strong>CEP:</strong>
                                        {{ item.cep || 'N/A' }}
                                    </div>
                                    <div>
                                        <strong>Email:</strong>
                                        {{ item.email || 'N/A' }}
                                    </div>
                                </div>
                            </v-tooltip>
                        </template>

                        <template v-slot:[`item.observacoes`]="{ item }">
                            <v-tooltip v-if="item.observacoes" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <span
                                        v-bind="attrs"
                                        v-on="on"
                                        class="text-truncate"
                                        style="max-width: 180px; display: inline-block;"
                                    >
                                        {{ item.observacoes }}
                                    </span>
                                </template>
                                <span>{{ item.observacoes }}</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        text
                        @click="fecharModalPreVisualizacao"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        color="success"
                        :loading="salvandoDados"
                        @click="salvarAlteracoes"
                    >
                        <v-icon class="mr-2">mdi-check</v-icon>
                        Salvar Alterações
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import TituloService from '@/services/TituloService';
import Vue from 'vue';

export default {
    name: 'UploadPreVisualizacao',
    props: {
        titulosSelecionados: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            arquivo: null,
            processandoArquivo: false,
            salvandoDados: false,
            dadosProcessados: [],
            filtroStatus: null,
            filtroTitulo: '',
            modalPreVisualizacao: false,
            tituloService: new TituloService(Vue.http, this.$store),
            headersPreVisualizacao: [
                { text: 'Linha', value: 'linha', width: '80px' },
                { text: 'Status', value: 'status', width: '100px' },
                { text: 'Erros', value: 'erros', width: '60px' },
                { text: 'ID Título', value: 'idTitulo' },
                { text: 'Nosso Número', value: 'nossoNumero' },
                { text: 'CD Apresentante', value: 'cdApresentante' },
                { text: 'Nome Devedor', value: 'nome' },
                { text: 'CPF/CNPJ', value: 'documento' },
                { text: 'Telefone', value: 'telefone' },
                { text: 'Principal', value: 'tipoFormatado' },
                {
                    text: 'Informações',
                    value: 'informacoes',
                    width: '100px',
                    sortable: false
                }
            ],
            headersModalPreVisualizacao: [
                { text: 'Ação', value: 'acao', width: '120px' },
                { text: 'ID Título', value: 'idTitulo' },
                { text: 'Nosso Número', value: 'nossoNumero' },
                { text: 'CD Apresentante', value: 'cdApresentante' },
                { text: 'Nome Devedor', value: 'nome' },
                { text: 'CPF/CNPJ', value: 'documento' },
                { text: 'Telefone', value: 'telefone' },
                { text: 'Principal', value: 'tipoFormatado', width: '120px' },
                {
                    text: 'Informações',
                    value: 'informacoes',
                    width: '100px',
                    sortable: false
                },
                { text: 'Observações', value: 'observacoes', width: '200px' }
            ],
            opcoesStatus: [
                { text: 'Todos', value: null },
                { text: 'Válidos', value: true },
                { text: 'Com Erros', value: false }
            ]
        };
    },
    computed: {
        dadosFiltrados() {
            let dados = this.dadosProcessados;

            if (this.filtroStatus !== null) {
                dados = dados.filter(item => item.valido === this.filtroStatus);
            }

            if (this.filtroTitulo) {
                dados = dados.filter(
                    item =>
                        item.idTitulo &&
                        item.idTitulo
                            .toLowerCase()
                            .includes(this.filtroTitulo.toLowerCase())
                );
            }

            return dados;
        },

        registrosValidos() {
            return this.dadosProcessados.filter(item => item.valido);
        },

        registrosComErro() {
            return this.dadosProcessados.filter(item => !item.valido);
        },

        titulosUnicos() {
            const titulos = new Set();
            this.dadosProcessados.forEach(item => {
                if (item.idTitulo) {
                    titulos.add(item.idTitulo);
                }
            });
            return Array.from(titulos);
        },

        registrosParaCriar() {
            return this.registrosValidos.filter(item => item.acao === 'criar');
        },

        registrosParaAtualizar() {
            return this.registrosValidos.filter(
                item => item.acao === 'atualizar'
            );
        },

        podePrevisualizar() {
            return (
                this.dadosProcessados.length > 0 &&
                this.registrosComErro.length === 0
            );
        }
    },
    methods: {
        onArquivoSelecionado() {
            this.dadosProcessados = [];
        },

        async processarArquivo() {
            if (!this.arquivo) return;

            this.processandoArquivo = true;

            try {
                const dados = await this.lerArquivoExcel();
                this.dadosProcessados = this.validarDados(dados);
            } catch (error) {
                console.error('Erro ao processar arquivo:', error);
                this.$emit(
                    'show-error',
                    'Erro ao processar arquivo. Verifique se o formato está correto.'
                );
            } finally {
                this.processandoArquivo = false;
            }
        },

        lerArquivoExcel() {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = e => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const sheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                            header: 1
                        });

                        resolve(jsonData);
                    } catch (error) {
                        reject(error);
                    }
                };

                reader.onerror = reject;
                reader.readAsArrayBuffer(this.arquivo);
            });
        },

        criarItemDados(linha, cabecalhos, numeroLinha) {
            const item = {
                index: numeroLinha,
                linha: numeroLinha,
                idTitulo: linha[0] || '', // ID TITULO
                nossoNumero: linha[1] || '', // NOSSO NUMERO
                cdApresentante: linha[2] || '', // CD_APRESENTANTE
                nome: linha[3] || '', // NOME DEVEDOR
                documento: linha[4] || '', // CPF/CNPJ
                endereco: linha[5] || '', // ENDERECO
                bairro: linha[6] || '', // BAIRRO
                cidade: linha[7] || '', // CIDADE
                uf: linha[8] || '', // UF
                cep: linha[9] || '', // CEP
                email: linha[10] || '', // EMAIL
                telefone: linha[11] || '', // TELEFONE
                principal: parseInt(linha[12]) || 0, // PRINCIPAL (1 ou 0)
                valido: true,
                erros: [],
                acao: null // Será definido pelo backend
            };

            this.validarItem(item);

            return item;
        },

        validarItem(item) {
            if (!item.idTitulo || item.idTitulo.toString().trim() === '') {
                item.erros.push('ID TITULO é obrigatório');
                item.valido = false;
            }

            if (item.nossoNumero && item.nossoNumero.toString().trim() === '') {
                item.erros.push(
                    'NOSSO NUMERO não pode estar vazio se informado'
                );
                item.valido = false;
            }

            if (
                item.cdApresentante &&
                item.cdApresentante.toString().trim() === ''
            ) {
                item.erros.push(
                    'CD APRESENTANTE não pode estar vazio se informado'
                );
                item.valido = false;
            }

            if (!item.nome || item.nome.toString().trim() === '') {
                item.erros.push('Nome é obrigatório');
                item.valido = false;
            } else {
                const nome = item.nome.toString().trim();
                if (nome.length < 2) {
                    item.erros.push('Nome deve ter pelo menos 2 caracteres');
                    item.valido = false;
                }
                // if (nome.length > 45) {
                //     item.erros.push('Nome deve ter no máximo 45 caracteres');
                //     item.valido = false;
                // }
            }

            if (!item.documento || item.documento.toString().trim() === '') {
                item.erros.push('CPF/CNPJ é obrigatório');
                item.valido = false;
            } else if (!this.validarDocumento(item.documento)) {
                item.erros.push('CPF/CNPJ inválido');
                item.valido = false;
            }

            if (!item.endereco || item.endereco.toString().trim() === '') {
                item.erros.push('Endereço é obrigatório');
                item.valido = false;
            } else {
                const endereco = item.endereco.toString().trim();
                if (endereco.length < 5) {
                    item.erros.push(
                        'Endereço deve ter pelo menos 5 caracteres'
                    );
                    item.valido = false;
                }
                if (endereco.length > 255) {
                    item.erros.push(
                        'Endereço deve ter no máximo 255 caracteres'
                    );
                    item.valido = false;
                }
            }

            if (!item.bairro || item.bairro.toString().trim() === '') {
                item.erros.push('Bairro é obrigatório');
                item.valido = false;
            } else {
                const bairro = item.bairro.toString().trim();
                if (bairro.length < 2) {
                    item.erros.push('Bairro deve ter pelo menos 2 caracteres');
                    item.valido = false;
                }
                if (bairro.length > 100) {
                    item.erros.push('Bairro deve ter no máximo 100 caracteres');
                    item.valido = false;
                }
            }

            if (!item.cidade || item.cidade.toString().trim() === '') {
                item.erros.push('Cidade é obrigatória');
                item.valido = false;
            } else {
                const cidade = item.cidade.toString().trim();
                if (cidade.length < 2) {
                    item.erros.push('Cidade deve ter pelo menos 2 caracteres');
                    item.valido = false;
                }
                if (cidade.length > 100) {
                    item.erros.push('Cidade deve ter no máximo 100 caracteres');
                    item.valido = false;
                }
                if (!/^[a-zA-ZÀ-ÿ\s\-'.]+$/.test(cidade)) {
                    item.erros.push('Cidade contém caracteres inválidos');
                    item.valido = false;
                }
            }

            // UF - obrigatório e validação
            if (!item.uf || item.uf.toString().trim() === '') {
                item.erros.push('UF é obrigatória');
                item.valido = false;
            } else if (!this.validarUF(item.uf)) {
                item.erros.push('UF inválida');
                item.valido = false;
            }

            if (!item.cep || item.cep.toString().trim() === '') {
                item.erros.push('CEP é obrigatório');
                item.valido = false;
            } else if (!this.validarCEP(item.cep)) {
                item.erros.push('CEP inválido (deve ter 8 dígitos)');
                item.valido = false;
            }

            if (!item.email || item.email.toString().trim() === '') {
                item.erros.push('E-mail é obrigatório');
                item.valido = false;
            } else {
                const email = item.email.toString().trim();
                if (email.length <= 1) {
                    item.erros.push('E-mail deve ter mais de 1 caractere');
                    item.valido = false;
                } else if (!this.validarEmail(email)) {
                    item.erros.push('E-mail inválido');
                    item.valido = false;
                } else if (email.length > 255) {
                    item.erros.push('E-mail deve ter no máximo 255 caracteres');
                    item.valido = false;
                }
            }

            if (!item.telefone || item.telefone.toString().trim() === '') {
                item.erros.push('Telefone é obrigatório');
                item.valido = false;
            } else {
                const telefone = item.telefone.toString().trim();
                if (telefone.length <= 1) {
                    item.erros.push('Telefone deve ter mais de 1 caractere');
                    item.valido = false;
                } else if (!this.validarTelefone(telefone)) {
                    item.erros.push(
                        'Telefone inválido (deve ter entre 10 e 11 dígitos)'
                    );
                    item.valido = false;
                }
            }

            if (item.principal !== 0 && item.principal !== 1) {
                item.erros.push('PRINCIPAL deve ser 0 ou 1');
                item.valido = false;
            }
        },

        validarDocumento(documento) {
            const limpo = documento.toString().replace(/[^a-zA-Z0-9]/g, '');
            const somenteNumeros = limpo.replace(/\D/g, '');

            if (limpo.length === 11 && somenteNumeros.length === 11) {
                return this.validarCPF(somenteNumeros);
            } else if (limpo.length === 14) {
                return this.validarCNPJ(limpo);
            }

            return false;
        },

        validarCPF(cpf) {
            if (/^(\d)\1{10}$/.test(cpf)) {
                return false;
            }

            let soma = 0;
            let resto;

            for (let i = 1; i <= 9; i++) {
                soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
            }
            resto = (soma * 10) % 11;
            if (resto === 10 || resto === 11) resto = 0;
            if (resto !== parseInt(cpf.substring(9, 10))) return false;

            soma = 0;

            for (let i = 1; i <= 10; i++) {
                soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
            }
            resto = (soma * 10) % 11;
            if (resto === 10 || resto === 11) resto = 0;
            if (resto !== parseInt(cpf.substring(10, 11))) return false;

            return true;
        },

        validarCNPJ(cnpj) {
            const stripped = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            if (stripped.length !== 14) return false;
            if (!/^[A-Z0-9]{12}\d{2}$/.test(stripped)) return false;

            const charToVal = c => /\d/.test(c) ? parseInt(c, 10) : c.toLowerCase().charCodeAt(0) - 87;
            const valores = stripped.split('').map(charToVal);

            if (new Set(valores).size === 1) return false;

            const peso1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
            const soma1 = valores.slice(0, 12).reduce((acc, v, i) => acc + v * peso1[i], 0);
            const resto1 = soma1 % 11;
            const digito1 = resto1 < 2 ? 0 : 11 - resto1;

            const peso2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
            const soma2 = valores.slice(0, 12).concat(digito1).reduce((acc, v, i) => acc + v * peso2[i], 0);
            const resto2 = soma2 % 11;
            const digito2 = resto2 < 2 ? 0 : 11 - resto2;

            return valores[12] === digito1 && valores[13] === digito2;
        },

        validarCEP(cep) {
            const somenteNumeros = cep.toString().replace(/\D/g, '');
            return somenteNumeros.length === 8 && somenteNumeros !== '00000000';
        },

        validarUF(uf) {
            const ufsValidas = [
                'AC',
                'AL',
                'AP',
                'AM',
                'BA',
                'CE',
                'DF',
                'ES',
                'GO',
                'MA',
                'MT',
                'MS',
                'MG',
                'PA',
                'PB',
                'PR',
                'PE',
                'PI',
                'RJ',
                'RN',
                'RS',
                'RO',
                'RR',
                'SC',
                'SP',
                'SE',
                'TO'
            ];
            return ufsValidas.includes(uf.toString().toUpperCase());
        },

        validarEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email.toString().trim());
        },

        validarTelefone(telefone) {
            const somenteNumeros = telefone.toString().replace(/\D/g, '');
            return somenteNumeros.length >= 10 && somenteNumeros.length <= 11;
        },

        validarDados(dados) {
            const dadosProcessados = [];

            const cabecalhos = dados[0] || [];

            for (let i = 1; i < dados.length; i++) {
                const linha = dados[i] || [];
                const item = this.criarItemDados(linha, cabecalhos, i + 1);
                dadosProcessados.push(item);
            }

            this.validarDadosCruzados(dadosProcessados);

            return dadosProcessados;
        },

        validarDadosCruzados(dados) {
            const dadosPorTitulo = {};

            dados.forEach(item => {
                if (!dadosPorTitulo[item.idTitulo]) {
                    dadosPorTitulo[item.idTitulo] = [];
                }
                dadosPorTitulo[item.idTitulo].push(item);
            });

            Object.keys(dadosPorTitulo).forEach(tituloId => {
                const registrosTitulo = dadosPorTitulo[tituloId];

                this.validarDocumentosUnicos(registrosTitulo);

                this.validarPrincipalUnico(registrosTitulo);
            });
        },

        validarDocumentosUnicos(registros) {
            const documentos = {};

            registros.forEach(item => {
                if (
                    !item.documento ||
                    item.documento.toString().trim() === ''
                ) {
                    return;
                }

                const doc = item.documento.replace(/[^a-zA-Z0-9]/g, '');
                if (documentos[doc]) {
                    item.erros.push('CPF/CNPJ duplicado para este título');
                    item.valido = false;
                    documentos[doc].erros.push(
                        'CPF/CNPJ duplicado para este título'
                    );
                    documentos[doc].valido = false;
                } else {
                    documentos[doc] = item;
                }
            });
        },

        validarPrincipalUnico(registros) {
            const principais = registros.filter(item => item.principal === 1);

            if (principais.length === 0) {
                registros.forEach(item => {
                    item.erros.push(
                        'É necessário ter exatamente um PRINCIPAL = 1 por título'
                    );
                    item.valido = false;
                });
            } else if (principais.length > 1) {
                principais.forEach(item => {
                    item.erros.push(
                        'Apenas um registro pode ter PRINCIPAL = 1 por título'
                    );
                    item.valido = false;
                });
            }
        },

        formatarDocumento(documento) {
            if (!documento) return '';

            const limpo = documento.toString().replace(/[^a-zA-Z0-9]/g, '');

            if (limpo.length === 11) {
                return limpo.replace(
                    /(\d{3})(\d{3})(\d{3})(\d{2})/,
                    '$1.$2.$3-$4'
                );
            } else if (limpo.length === 14) {
                return limpo.replace(
                    /^(.{2})(.{3})(.{3})(.{4})(.{2})$/,
                    '$1.$2.$3/$4-$5'
                );
            }

            return documento;
        },

        getCorAcao(acao) {
            switch (acao) {
                case 'criar':
                    return 'success';
                case 'atualizar':
                    return 'warning';
                case 'sem_alteracao':
                    return 'grey';
                default:
                    return 'primary';
            }
        },

        formatarAcao(acao) {
            switch (acao) {
                case 'criar':
                    return 'CRIAR';
                case 'atualizar':
                    return 'ATUALIZAR';
                case 'sem_alteracao':
                    return 'SEM ALTERAÇÃO';
                default:
                    return acao ? acao.toUpperCase() : '';
            }
        },

        async confirmarProcessamento() {
            if (this.registrosValidos.length === 0) {
                this.$emit(
                    'show-error',
                    'Nenhum registro válido para processar.'
                );
                return;
            }

            this.salvandoDados = true;

            try {
                await new Promise(resolve => setTimeout(resolve, 2000));

                this.$emit(
                    'show-success',
                    `${this.registrosValidos.length} devedores/coodevedores processados com sucesso!`
                );
                this.limparDados();
            } catch (error) {
                console.error('Erro ao salvar dados:', error);
                this.$emit(
                    'show-error',
                    'Erro ao processar os dados. Tente novamente.'
                );
            } finally {
                this.salvandoDados = false;
            }
        },

        async abrirModalPreVisualizacao() {
            if (this.registrosComErro.length > 0) {
                this.$emit(
                    'show-error',
                    `Não é possível pré-visualizar alterações. Existem ${this.registrosComErro.length} registro(s) com erro(s) que precisam ser corrigidos.`
                );
                return;
            }

            if (this.registrosValidos.length === 0) {
                this.$emit(
                    'show-error',
                    'Nenhum registro válido para visualizar.'
                );
                return;
            }

            if (!this.arquivo) {
                this.$emit(
                    'show-error',
                    'Arquivo não encontrado para processamento.'
                );
                return;
            }

            try {
                this.processandoArquivo = true;

                const response = await this.tituloService.processarArquivoDevedores(
                    this.arquivo,
                    0
                );

                if (response.data && response.data.success) {
                    this.atualizarDadosComBackend(response.data.dados);
                    this.modalPreVisualizacao = true;
                } else {
                    this.$emit(
                        'show-error',
                        'Erro ao processar arquivo no servidor.'
                    );
                }
            } catch (error) {
                console.error('Erro ao chamar backend:', error);
                this.$emit(
                    'show-error',
                    'Erro ao comunicar com o servidor. Tente novamente.'
                );
            } finally {
                this.processandoArquivo = false;
            }
        },

        atualizarDadosComBackend(dadosBackend) {
            dadosBackend.forEach(itemBackend => {
                const itemLocal = this.dadosProcessados.find(
                    item => item.linha === itemBackend.linha
                );

                if (itemLocal) {
                    itemLocal.acao = itemBackend.status;

                    if (itemBackend.observacoes) {
                        itemLocal.observacoes = itemBackend.observacoes;
                    }

                    if (itemBackend.erros && itemBackend.erros.length > 0) {
                        itemLocal.erros = [
                            ...itemLocal.erros,
                            ...itemBackend.erros
                        ];
                        itemLocal.valido = false;
                    }
                }
            });
        },

        fecharModalPreVisualizacao() {
            this.modalPreVisualizacao = false;
        },

        async salvarAlteracoes() {
            this.salvandoDados = true;

            try {
                const response = await this.tituloService.processarArquivoDevedores(
                    this.arquivo,
                    1
                );

                if (response.data && response.data.success) {
                    this.$emit(
                        'show-success',
                        `${this.registrosValidos.length} devedores/coodevedores salvos com sucesso!`
                    );
                    this.$emit('refazer-busca');
                    this.fecharModalPreVisualizacao();
                    this.limparDados();
                } else {
                    this.$emit(
                        'show-error',
                        response.data.message || 'Erro ao salvar as alterações.'
                    );
                }
            } catch (error) {
                console.error('Erro ao salvar dados:', error);
                this.$emit(
                    'show-error',
                    'Erro ao salvar as alterações. Tente novamente.'
                );
            } finally {
                this.salvandoDados = false;
            }
        },

        limparDados() {
            this.arquivo = null;
            this.dadosProcessados = [];
            this.filtroStatus = null;
            this.filtroTitulo = '';
            this.modalPreVisualizacao = false;
        },

        baixarRelatorioErros() {
            const dadosErros = this.registrosComErro.map(item => ({
                Linha: item.linha,
                'ID Título': item.idTitulo,
                'Nosso Número': item.nossoNumero,
                'Nome Devedor': item.nome,
                'CPF/CNPJ': item.documento,
                Endereço: item.endereco,
                Bairro: item.bairro,
                Cidade: item.cidade,
                UF: item.uf,
                CEP: item.cep,
                Email: item.email,
                Telefone: item.telefone,
                Principal: item.principal === 1 ? 'Sim' : 'Não',
                'CD Apresentante': item.cdApresentante,
                Erros: item.erros.join('; ')
            }));

            const worksheet = XLSX.utils.json_to_sheet(dadosErros);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Erros');

            XLSX.writeFile(workbook, 'relatorio_erros_devedores.xlsx');
        }
    }
};
</script>

<style scoped>
.v-card {
    border-radius: 8px;
}

.text--disabled {
    color: rgba(0, 0, 0, 0.38) !important;
}
</style>
